import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import {
  CalculateClosingDisclosureProps,
  calculateTaxFactor,
  calculateTransferTaxFactor,
  chooseMarketInterestRate,
} from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

// Enhanced interest rate calculation for DSCR loans based on LTV, credit score, and DSCR ratio
const calculateDSCRInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  dscr: number,
  state?: RootState
) => {
  // If we have access to the Redux state, use the enhanced calculation
  if (state) {
    return calculateEnhancedInterestRate(
      state,
      'DSCR',
      baseRate,
      creditScore,
      ltv
    );
  }

  // Fallback to original calculation if no state provided
  let rateAdjustment = 0;

  // Credit score adjustments for DSCR loans
  if (creditScore >= 740) {
    rateAdjustment -= 0.25;
  } else if (creditScore >= 720) {
    rateAdjustment -= 0.125;
  } else if (creditScore >= 700) {
    rateAdjustment -= 0.0625;
  } else if (creditScore >= 680) {
    // No change
  } else if (creditScore >= 660) {
    rateAdjustment += 0.125;
  } else if (creditScore >= 640) {
    rateAdjustment += 0.25;
  } else if (creditScore >= 620) {
    rateAdjustment += 0.375;
  } else if (creditScore >= 600) {
    rateAdjustment += 0.5;
  } else if (creditScore >= 580) {
    rateAdjustment += 0.625;
  } else if (creditScore >= 560) {
    rateAdjustment += 0.75;
  } else if (creditScore >= 540) {
    rateAdjustment += 0.875;
  } else if (creditScore >= 520) {
    rateAdjustment += 1.0;
  } else {
    rateAdjustment += 1.125;
  }

  // LTV adjustments for DSCR loans
  if (ltv > 0.85) {
    rateAdjustment += 0.5;
  } else if (ltv > 0.8) {
    rateAdjustment += 0.375;
  } else if (ltv > 0.75) {
    rateAdjustment += 0.25;
  } else if (ltv > 0.7) {
    rateAdjustment += 0.125;
  } else {
    // LTV <= 70%, no additional adjustment
  }

  // DSCR ratio adjustments
  if (dscr >= 1.5) {
    rateAdjustment -= 0.25;
  } else if (dscr >= 1.35) {
    rateAdjustment -= 0.125;
  } else if (dscr >= 1.25) {
    rateAdjustment -= 0.0625;
  } else if (dscr >= 1.15) {
    // No change
  } else if (dscr >= 1.1) {
    rateAdjustment += 0.125;
  } else if (dscr >= 1.05) {
    rateAdjustment += 0.25;
  } else {
    rateAdjustment += 0.375;
  }

  return Math.max(0, baseRate + rateAdjustment);
};

export const calculateDSCRClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    marketInterestRate = 6.75,
    creditScore = 0,
    rent = 0,
    loanTerm = 30,
    removePrepaymentPenalty = false,
  }: CalculateClosingDisclosureProps,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);
  const transferTaxFactor = calculateTransferTaxFactor(zipCode);

  // Enhanced interest rate calculation for DSCR
  let interestRate = calculateDSCRInterestRate(
    marketInterestRate,
    creditScore,
    ltv,
    0, // Will be calculated after we have the PITI
    state
  );

  // Apply prepayment penalty removal adjustments
  if (removePrepaymentPenalty) {
    interestRate += 0.5; // Increase rate by 0.5% when removing prepayment penalty
  }

  //change on user input on account page
  const numberOfPayments = loanTerm * 12;

  // constant fees
  const countyRecordingFees = 250;
  const stateRecordingFees = 150;
  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const appraisalFeeBase = 550;
  const underwritingFee = 1295;
  const applicationFeeBase = 995;
  const recordingTitle = 75;
  const recordingMortgage = 75;
  const surveyFee = 250;
  const pestInspectionFee = 145;
  const rentalIncomeReportFee = 185;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const creditReportFeeBase = 150;
  const processingFeeBase = 795;
  const titleSettlementAgentFee = 350;
  const earnestMoneyDeposit = 1000;
  const insuranceBinder = 150;
  const initialEscrowMortgageInsurance = 0;
  const prepaidsPropertyTaxes = -100; //this is a credit from the buyer

  // factored calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;

  //UNFACTORED CALCULATIONS, NEED TO FIGURE OUT HOW TO INCLUDE CREDIT AND STATE FACTORS

  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575; //State, Credit
  const minimumOwnersTitle = 650;
  const maximumOwnersTitle = 3850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );
  const recordingTransferTaxBase = purchasePrice * 0.01; //State dependent.

  const monthlyHomeownersInsurance = (purchasePrice * 0.01035) / 12;
  const upfrontHomeownersInsurance = purchasePrice * 0.01035;
  const monthlyPropertyTaxes = annualPropertyTax / 12;
  const prepaidsHomeownersInsurance = ((purchasePrice * 0.01035) / 12) * 3;
  const titleLendersTitleInsuranceBase = purchasePrice * 0.0045;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const minimumInspectionFee = 650;
  const maximumInspectionFee = 4500;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, minimumInspectionFee),
    maximumInspectionFee
  );
  const initialEscrowTaxes = monthlyPropertyTaxes * 6; //
  const initialEscrowHOI = ((purchasePrice * 0.01) / 12) * 3;
  const estimatedEscrow = monthlyHomeownersInsurance + monthlyPropertyTaxes;

  //these aren't going to change, they are determined
  const dailyInterestRate = interestRate / 365 / 100; // Convert annual rate to daily decimal
  const prepaidInterest = loanAmount * dailyInterestRate * 15; // Return the result rounded to 2 decimal places
  const monthlyInterestRate = interestRate / 12 / 100; // Convert annual rate to monthly decimal
  const principalAndInterest =
    (loanAmount *
      (monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  const piti = principalAndInterest + estimatedEscrow;
  const transferTaxes = purchasePrice * transferTaxFactor;
  const cleanPayment = piti;
  const cashFlow = rent - cleanPayment - hoaFees;
  const dscr = rent / (piti + hoaFees);

  // Dynamic lender credits based on DSCR and LTV
  let lenderCredits = 0;
  if (dscr >= 1.5 && ltv <= 0.75) {
    lenderCredits = loanAmount * 0.02; // 2% credit for excellent DSCR and low LTV
  } else if (dscr >= 1.35 && ltv <= 0.8) {
    lenderCredits = loanAmount * 0.015; // 1.5% credit for good DSCR and moderate LTV
  } else if (dscr >= 1.25 && ltv <= 0.85) {
    lenderCredits = loanAmount * 0.01; // 1% credit for acceptable DSCR and higher LTV
  } else {
    lenderCredits = loanAmount * 0.005; // 0.5% base credit
  }

  // Prepayment penalty calculations for DSCR loans
  const prepaymentPenaltySchedule = [5, 4, 3, 2, 1]; // 5%/4%/3%/2%/1%
  const prepaymentPenaltyYear1 = removePrepaymentPenalty
    ? 0
    : (loanAmount * prepaymentPenaltySchedule[0]) / 100;
  const prepaymentPenaltyYear2 = removePrepaymentPenalty
    ? 0
    : (loanAmount * prepaymentPenaltySchedule[1]) / 100;
  const prepaymentPenaltyYear3 = removePrepaymentPenalty
    ? 0
    : (loanAmount * prepaymentPenaltySchedule[2]) / 100;
  const prepaymentPenaltyYear4 = removePrepaymentPenalty
    ? 0
    : (loanAmount * prepaymentPenaltySchedule[3]) / 100;
  const prepaymentPenaltyYear5 = removePrepaymentPenalty
    ? 0
    : (loanAmount * prepaymentPenaltySchedule[4]) / 100;

  // Additional lender origination fee when removing prepayment penalty
  const additionalLenderOriginationFee = removePrepaymentPenalty
    ? loanAmount * 0.01
    : 0; // 1% of loan amount

  const titleCosts =
    optionalOwnersTitleInsurance +
    insuranceBinder +
    surveyFee +
    titleSettlementAgentFee +
    titleLendersTitleInsuranceBase +
    titleTitleSearchBase;
  const lenderCostTotals =
    rentalIncomeReportFee +
    floodDeterminationFee +
    floodMonitoringFee +
    creditReportFeeBase +
    appraisalFeeBase +
    applicationFeeBase +
    underwritingFee +
    processingFeeBase +
    lenderCredits;
  const govCosts =
    stateRecordingFees +
    countyRecordingFees +
    recordingMortgage +
    recordingTitle +
    recordingDeedBase +
    transferTaxes;
  const prepaidCosts =
    initialEscrowTaxes +
    prepaidsPropertyTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance +
    upfrontHomeownersInsurance;
  const closingCosts = titleCosts + lenderCostTotals + govCosts + prepaidCosts;
  const cashToClose = closingCosts + downPayment + earnestMoneyDeposit;
  const cashOutsideClosing =
    appraisalFeeBase +
    rentalIncomeReportFee +
    earnestMoneyDeposit +
    homeInspectionFee;
  const unbeatableCredit = (purchasePrice * commission) / 100; //this is where we put the buyers agent commission
  const unbeatableDeal = cashToClose - unbeatableCredit;

  // Perform calculations using the provided purchase price and constants
  // Prepare the closing disclosure object
  const closingDisclosure = {
    type: USER_LOAN_PROGRAM.DSCR,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    ltv,

    cleanPayment,
    cashOutsideClosing,
    processingFeeBase,
    principalAndInterest,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti,
    estimatedEscrow,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest,
    upfrontHomeownersInsurance,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase,
    underwritingFee,
    applicationFeeBase,
    pestInspectionFee,
    floodMonitoringFee,
    floodDeterminationFee,
    creditReportFeeBase,
    recordingTitle,
    recordingMortgage,
    countyRecordingFees,
    stateRecordingFees,
    surveyFee,
    initialEscrowTaxes,
    initialEscrowMortgageInsurance,
    initialEscrowHOI,
    rentalIncomeReportFee,
    lenderCredits,
    lenderCostTotals,
    rent,
    titleCosts,
    govCosts,
    prepaidCosts,
    closingCosts,
    cashToClose,
    unbeatableDeal,
    dscr,
    cashFlow,
    transferTaxes,

    // Prepayment penalty fields
    removePrepaymentPenalty,
    prepaymentPenaltyYear1,
    prepaymentPenaltyYear2,
    prepaymentPenaltyYear3,
    prepaymentPenaltyYear4,
    prepaymentPenaltyYear5,
    additionalLenderOriginationFee,
  };
  return closingDisclosure;
};

export type ProductDSCREsitmation = ReturnType<
  typeof calculateDSCRClosingDisclosure
>;
